import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import styles from "./RewardTierCard.module.scss";
import CheckedCircleIcon from "../../components/icon-components/CheckedCircleIcon";
import CircleOutlineIcon from "../../components/icon-components/CircleOutlineIcon";
import RedeemAwardsIllustrationIcon from "../../components/icon-components/RedeemAwardsIllustrationIcon";
import { formatDateMonthDateYear } from "../../utility/functions/common-functions";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import { PEACH } from "../../utility/constants/common-constants";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import RewardTierCardSkeleton from "./RewardTierCardSkeleton";
import { TIER_STATUS } from "../../utility/constants/locale";
import { useNavigate } from "react-router-dom";

interface RewardTierCardProps {
  data: Array<{
    tierClassificationId: number;
    tierDisplayName: string;
    tierSequence: number;
    rangeFrom: number;
    rangeTo: number | null;
    validTill: string | null;
    redeemedDate: string | null;
    statusCode: string;
    statusDisplayName: string;
    statusName: string;
    redemptionId?: string;
  }>;
  optinBonus?: number;
  isLoading: boolean;
  error: unknown;
}

const RewardTierCard: React.FC<RewardTierCardProps> = ({
  data,
  optinBonus,
  isLoading,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Replace the `rangeFrom` value of the first tier if it's 0 and optinBonus exists
  const processedTiers = data.map((tier, index) => {
    if (index === 0 && tier.rangeFrom === 0 && typeof optinBonus === "number") {
      return { ...tier, rangeFrom: optinBonus };
    }
    return tier;
  });

  const handleFaqClick = () => {
    navigate("/patient/faq");
  };

  return (
    <DataLoader
      isLoading={isLoading}
      error={error}
      data={data}
      loadingComponent={<RewardTierCardSkeleton />}
      emptyComponent={<Typography>{t("noData")}</Typography>}
      errorComponent={
        <Typography color="error">
          {t("myRewardsPage.errorLoadingData")}
        </Typography>
      }
    >
      {() => (
        <Box className={styles.cardContainer}>
          <Box className={styles.header}>
            <Typography variant="h3">{t("myRewardsPage.title")}</Typography>
          </Box>

          <List
            sx={{
              padding: "1rem 4rem",
              "@media (max-width: 900px)": {
                padding: "1rem 0",
              },
            }}
          >
            {processedTiers.map((tier) => {
              const isCompleted = tier.statusName === TIER_STATUS.COMPLETED;
              const isEligible = tier.statusName === TIER_STATUS.ELIGIBLE;

              return (
                <ListItem
                  key={tier.tierClassificationId}
                  className={styles.tierItem}
                >
                  <Box className={styles.tierBox}>
                    <Box className={styles.tierRow}>
                      <ListItemIcon className={styles.iconWrapper}>
                        {isCompleted || isEligible ? (
                          <CheckedCircleIcon
                            variant="completed"
                            width={16}
                            height={16}
                          />
                        ) : (
                          <CircleOutlineIcon width={16} height={16} />
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          tier.tierClassificationId === 5 ? (
                            <Typography variant="subtitle2" component="span">
                              {tier.tierDisplayName}
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="subtitle2" component="span">
                                {tier.rangeFrom} {t("myRewardsPage.points")}:{" "}
                              </Typography>
                              <Typography variant="body1" component="span">
                                {tier.tierDisplayName}
                              </Typography>
                            </>
                          )
                        }
                      />
                    </Box>

                    {tier.redeemedDate && (
                      <Typography
                        variant="bodySmall"
                        color="text.secondary"
                        ml={"30px"}
                      >
                        {t("myRewardsPage.redeemedOn")}{" "}
                        {formatDateMonthDateYear(tier.redeemedDate)}
                      </Typography>
                    )}

                    {isEligible && (
                      <Box className={styles.claimBox}>
                        <RedeemAwardsIllustrationIcon />
                        <Box className={styles.claimTextContent}>
                          <Typography
                            variant="bodySmall"
                            fontWeight={700}
                            textTransform="uppercase"
                            color={PEACH}
                            letterSpacing="1px"
                            textAlign="center"
                          >
                            {t("myRewardsPage.award", {
                              name: tier.tierDisplayName,
                            })}
                          </Typography>

                          <Typography
                            variant="bodySmall"
                            color="common.white"
                            mb={1}
                          >
                            {t("myRewardsPage.expiresOn")}{" "}
                            {tier.validTill
                              ? formatDateMonthDateYear(tier.validTill)
                              : "--"}
                          </Typography>

                          <CustomButton
                            variant="contained"
                            onClick={handleFaqClick}
                            text={t("myRewardsPage.howToRedeem")}
                            colorScheme="whiteContained"
                            sx={{ marginBottom: "1rem" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </DataLoader>
  );
};

export default RewardTierCard;
